import { defineRule } from 'vee-validate'
import { email, min, max, digits, alpha_num, regex } from '@vee-validate/rules'
import { formatCurrency } from '@js/utilities/number.js'
import dayjs from 'dayjs'
import Bugsnag from '@bugsnag/js'
import { url } from '@js/utilities/url.js'

// lodash components
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import replace from 'lodash/replace'
import split from 'lodash/split'
import toString from 'lodash/toString'
import toNumber from 'lodash/toNumber'
import padStart from 'lodash/padStart'

function defineValidationRules() {
    // NOSONAR
    const territory = process.env.TERRITORY

    defineRule('email', email)
    defineRule('min', min)
    defineRule('max', max)
    defineRule('digits', digits)
    defineRule('alpha_num', alpha_num)
    defineRule('regex', regex)

    defineRule('required', (value, [target], ctx) => {
        return !isUndefined(value) && !isNull(value) && toString(value).length > 0
    })

    defineRule('startsWith', (value, param) => {
        if (isUndefined(value) || isNull(value)) {
            return false
        }

        let needles = param
        if (!Array.isArray(needles)) {
            needles = [needles]
        }

        let result = false
        for (const needle of needles) {
            if (toString(value).startsWith(needle)) {
                result = true
                break
            }
        }

        return result
    })

    defineRule('checkboxRequired', value => {
        return !isUndefined(value) && !isNull(value) && toString(value).length && value !== false
    })

    defineRule('requiredIfEmpty', (value, [target], ctx) => {
        if (!isUndefined(value) && !isNull(value) && toString(value).length) {
            return true
        }

        const targetValue = ctx.form[target]
        return !isUndefined(targetValue) && !isNull(targetValue) && toString(targetValue).length
    })

    defineRule('buildingName', (value, params, ctx) => {
        if (
            !isUndefined(value) &&
            !isNull(value) &&
            toString(value).length &&
            toString(value).length >= params.min &&
            toString(value).length <= params.max
        ) {
            return true
        }

        const targetValue = ctx.form[params.target]
        return !isUndefined(targetValue) && !isNull(targetValue) && toString(targetValue).length
    })

    const ruleErrors = {
        INVALID: 'invalid',
        ERROR: 'error',
    }

    const ruleErrorField = {
        EMAIL: 'email',
        PHONE: 'phone',
    }

    defineRule('loqateEmail', async value => {
        try {
            const response = await window.axios.post(url(`/api/validate/email/`), { email: value })

            if (response.data) {
                return response.data
            }

            return `${ruleErrors.INVALID} ${ruleErrorField.EMAIL}`
        } catch (error) {
            if (window.BUGSNAG_VUE_API_KEY) {
                Bugsnag.notify(error)
            }

            return `${ruleErrors.ERROR} ${ruleErrorField.EMAIL}`
        }
    })

    defineRule('loqateMobilePhone', async value => {
        try {
            const response = await window.axios.post(url(`/api/validate/phone/`), { phone: value })

            if (response.data) {
                return response.data
            }

            return `${ruleErrors.INVALID} ${ruleErrorField.PHONE}`
        } catch (error) {
            if (window.BUGSNAG_VUE_API_KEY) {
                Bugsnag.notify(error)
            }

            return `${ruleErrors.ERROR} ${ruleErrorField.PHONE}`
        }
    })

    defineRule('validDate', value => {
        const dayMonthYear = split(value, '/')

        const day = territory !== 'usa' ? dayMonthYear[0] : dayMonthYear[1]
        const month = territory !== 'usa' ? dayMonthYear[1] : dayMonthYear[0]
        const year = dayMonthYear[2]
        const date = new Date(year, month - 1, day)

        if (
            date.getFullYear() === parseInt(year) &&
            date.getMonth() + 1 === parseInt(month) &&
            date.getDate() === parseInt(day) &&
            padStart(date.getFullYear(), 4, 0) > 1900
        ) {
            return true
        }

        return 'Please enter a valid date'
    })

    defineRule('dobBetween', (value, params) => {
        const dayMonthYear = split(value, '/')

        const day = territory !== 'usa' ? dayMonthYear[0] : dayMonthYear[1]
        const month = territory !== 'usa' ? dayMonthYear[1] : dayMonthYear[0]
        const year = dayMonthYear[2]
        const diff = dayjs().diff(`${year}-${month}-${day}`, 'years')

        if (diff >= params.min) {
            return true
        }

        return `So sorry! Due to lender restrictions, we can only process applications from people aged between ${params.min}-${params.max}.`
    })

    defineRule('pesel', value => {
        const stringValue = value.toString()
        const monthWithCentury = Number(stringValue.substring(2, 4))

        if (!monthWithCentury || monthWithCentury % 20 > 12) {
            return false
        }

        const day = Number(stringValue.substring(4, 6))
        if (!day || day < 1 || day > 31) {
            return false
        }

        const times = [1, 3, 7, 9]
        const digits = `${value}`.split(``).map(digit => {
            return parseInt(digit, 10)
        })

        const [dig11] = digits.splice(-1)

        const control =
            digits.reduce((previousValue, currentValue, index) => {
                return previousValue + currentValue * times[index % 4]
            }) % 10

        return 10 - (control === 0 ? 10 : control) === dig11
    })

    defineRule('polishIdentityCard', value => {
        // Check length.
        if (!value || value.length !== 9) {
            return false
        }

        const upperNum = value.toUpperCase()
        const letterValues = [
            '0',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            'A',
            'B',
            'C',
            'D',
            'E',
            'F',
            'G',
            'H',
            'I',
            'J',
            'K',
            'L',
            'M',
            'N',
            'O',
            'P',
            'Q',
            'R',
            'S',
            'T',
            'U',
            'V',
            'W',
            'X',
            'Y',
            'Z',
        ]

        const getLetterValue = letter => {
            for (let j = 0, max = letterValues.length; j < max; j++) {
                if (letter === letterValues[j]) {
                    return j
                }
            }

            return -1
        }

        // Check series.
        for (let i = 0; i < 3; ++i) {
            if (getLetterValue(upperNum[i]) < 10) {
                return false
            }
        }

        // Check number.
        for (let i = 3; i < 9; ++i) {
            if (getLetterValue(upperNum[i]) < 0 || getLetterValue(upperNum[i]) > 9) {
                return false
            }
        }

        // Calculate checksum.
        let sum =
            7 * getLetterValue(upperNum[0]) +
            3 * getLetterValue(upperNum[1]) +
            Number(getLetterValue(upperNum[2])) +
            7 * getLetterValue(upperNum[4]) +
            3 * getLetterValue(upperNum[5]) +
            Number(getLetterValue(upperNum[6])) +
            7 * getLetterValue(upperNum[7]) +
            3 * getLetterValue(upperNum[8])

        sum %= 10

        return sum === getLetterValue(upperNum[3])
    })

    defineRule('nip', value => {
        const nipWithoutDashes = value.toString().replace(/-/gu, ``)
        const reg = /^[0-9]{10}$/u

        if (!reg.test(nipWithoutDashes)) {
            return false
        }

        const dig = String(nipWithoutDashes).split(``)
        const controlValues = [6, 5, 7, 2, 3, 4, 5, 6, 7]
        const partialSums = controlValues.map((controlValue, index) => {
            return controlValue * parseInt(dig[index], 10)
        })

        let sum = 0
        partialSums.forEach(partialSum => {
            sum += partialSum
        })

        const control = sum % 11
        return parseInt(dig[9], 10) === control
    })

    defineRule('alphaWithDashes', value => {
        const pattern = new RegExp('^[A-Za-z][A-Za-z-]*[A-Za-z]$')

        if (pattern.test(value)) {
            return true
        }

        return 'Only alpha characters and dashes are allowed.'
    })

    defineRule('englishName', value => {
        const pattern = /^[\s]*[a-zA-Z]+(?:[-'\s][a-zA-Z]+)*[\s]*$/g

        if (pattern.test(value)) {
            return true
        }

        return 'Only alpha characters, dashes, apostrophes and spaces (includes trailing spacers) are allowed.'
    })

    defineRule('stringMinWithoutSpace', value => {
        if (!(value && value.length > 1)) {
            return 'Please enter at least 2 alpha characters'
        }
        const minValue = value.trim()
        if (minValue.length >= 2) {
            return true
        }
    })

    defineRule('ukPostcode', value => {
        const pattern = /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/g

        if (pattern.test(value)) {
            return true
        }

        return 'Only valid postcodes are allowed.'
    })

    defineRule('socialSecurityNumber', async value => {
        try {
            const response = await window.axios.get(url('/api/validate/ssn'))
            const pattern = new RegExp(
                /^(?:000-12-3000\b)|(?!\b(\d)\1+-(\d)\1+-(\d)\1+\b)(?!123-45-6789|219-09-9999|078-05-1120|123-12-1234)(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}/i,
            )

            if (!response.data) {
                return true
            }

            if (pattern.test(value)) {
                return true
            }
        } catch (error) {
            if (window.BUGSNAG_VUE_API_KEY) {
                Bugsnag.notify(error)
            }
        }

        return 'Social security number is not valid'
    })

    defineRule('amountBetween', (value, params) => {
        value = replace(value, /\s/g, '')
        value = toNumber(replace(value, /,/gi, ''))

        let min = toNumber(params.min)
        let max = toNumber(params.max)

        if (value >= min && value <= max) {
            return true
        }

        min = formatCurrency(min)
        max = formatCurrency(max)

        return `${params.errorMessage} ${min} - ${max}.`
    })

    /**
     * Matches US phone number format
     *
     * where the area code may not start with 1 and the prefix may not start with 1
     * allows '-' or ' ' as a separator and allows parens around area code
     * some people may want to put a '1' in front of their number
     *
     * 1(212)-999-2345 or
     * 212 999 2344 or
     * 212-999-0983
     *
     * but not
     * 111-123-5434
     * and not
     * 212 123 4567
     */
    defineRule('phoneUS', value => {
        const pattern = new RegExp(
            /^(\+?1-?)?(\([2-9]([02-9]\d|1[02-9])\)|[2-9]([02-9]\d|1[02-9]))-?[2-9]([02-9]\d|1[02-9])-?\d{4}$/,
        )

        if (pattern.test(value)) {
            return true
        }

        return 'Please enter a valid US phone number.'
    })

    defineRule('minAnnualSalary', (value, params) => {
        value = toNumber(replace(value, /,/gi, ''))
        let validationValue = toNumber(replace(params.value, /,/gi, ''))

        if (value <= params.min && (!validationValue || (validationValue && value !== validationValue))) {
            return 'errorAnnualSalary'
        }

        return true
    })
}

export { defineValidationRules }
